<template>
    <div class="mx-auto">
        <!-- Search and Filters -->
        <div class="flex justify-between mb-5 items-end">
            <div class="flex gap-2 w-full">
                <!-- <button aria-label="button" class="btn btn-primary rounded-lg" @click="onCreateService">
                    <button
                        aria-label="button"
                        type="button"
                        class="inline-flex items-center rounded-lg border border-transparent bg-primary p-0.5 text-white shadow-xs hover:bg-primary mr-1">
                        <Plus class="h-5 w-5" />
                    </button>
                    <span class="mx-1 align-super font-medium text-sm">Add</span>
                </button> -->

                <SearchInput
                    :loading="generalLoading"
                    placeholder="Search by name..."
                    :custom-class="'w-1/3'"
                    @search="(input) => (searchQuery = input)" />
            </div>

            <!-- <SelectDropdown
                v-can-access.permissions="[$Permissions.SWITCH_SERVICE]"
                class="justify-end"
                :model-value="currentSwitchedService"
                :options="servicesSelection"
                :loading="generalLoading || anyServiceIsSwitching"
                label="Switch service"
                :placeholder="'Select service'"
                @update:model-value="
                    currentSwitchedService = $event;
                    toggleMainService($event);
                ">
            </SelectDropdown> -->
        </div>

        <div v-if="anyServiceIsSwitching" class="fixed inset-0 flex items-center justify-center lg:pl-[254px]" style="z-index: 900">
            <div class="absolute inset-0 bg-black opacity-50"></div>
            <div class="relative bg-card rounded-lg shadow-lg w-1/3 items-center justify-center p-10">
                <AppLoader :force-loading="true" :message="'Service is being switched...'" default-height="inherit" :default-width="'100%'" />
            </div>
        </div>

        <ScrollArea class="w-full border shadow-md rounded-md mt-2" :class="(filteredServices?.length || 0) <= 10 ? 'h-auto' : 'h-70-dynamic'">
                <Table>
                    <TableHeader class="sticky top-0 bg-background z-10">
                        <TableRow class="bg-muted/50">
                            <TableHead class="first:rounded-tl-md">Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Created On</TableHead>
                            <TableHead>State</TableHead>
                            <TableHead>Cloud</TableHead>
                            <TableHead>Region</TableHead>
                            <TableHead class="last:rounded-tr-md"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <!-- Skeleton rows when loading -->
                        <template v-if="generalLoading">
                            <TableRow v-for="n in 5" :key="`skeleton-${n}`">
                                <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                <TableCell><SkeletonLoader preset="table-type" :loading="true" /></TableCell>
                                <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                            </TableRow>
                        </template>
                        <!-- Actual data rows -->
                        <template v-else>
                                <TableRow v-for="(service, serviceIndex) in filteredServices" :key="service.id">
                                    <TableCell class="font-medium">
                                        <div class="flex items-center gap-2">
                                            {{ service.name }}
                                            <Badge v-if="service.isCurrent" variant="default"> CURRENT </Badge>
                                            <Button
                                                v-else
                                                variant="outline"
                                                size="sm"
                                                @click="toggleMainService(service.id)">
                                                Switch to this
                                            </Button>
                                        </div>
                                    </TableCell>
                                    <TableCell class="text-xs">
                                        {{ service.type }}
                                        <b v-if="service.is_byoc"> (BYOC) </b>
                                    </TableCell>
                                    <TableCell>{{ service.created_timestamp }}</TableCell>
                                    <TableCell>
                                        <Badge
                                            variant="outline"
                                            :class="getStatusBadgeClasses(service.state)">
                                            {{ service.state.toUpperCase() }}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Avatar class="h-6 w-6 bg-background dark:bg-slate-200 border border-border rounded-sm shrink-0">
                                            <AvatarImage
                                                :src="`/icons/${service.cloud_provider}.svg`"
                                                :alt="service.cloud_provider"
                                                class="object-contain p-0.5" />
                                            <AvatarFallback class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                                                {{ service.cloud_provider?.charAt(0).toUpperCase() }}
                                            </AvatarFallback>
                                        </Avatar>
                                    </TableCell>
                                    <TableCell>{{ service.region }}</TableCell>
                                    <TableCell class="sticky right-0 z-10">
                                        <ActionMenu
                                            variant="menu"
                                            :show-api-tokens="true"
                                            :show-copy-id="true"
                                            :show-copy-kafka-brokers="true"
                                            @apiTokens="openTokensModal(service)"
                                            @copyKafkaBrokers="copyKafkaBrokers(service)"
                                            @copyId="copyId(service.id)" />
                                    </TableCell>
                                </TableRow>
                                <TableEmpty v-if="!generalLoading && !filteredServices?.length" :colspan="7">
                                    <TableEmptyState />
                                </TableEmpty>
                            </template>
                        </TableBody>
                    </Table>
                    <ScrollBar orientation="horizontal" />
                    <ScrollBar orientation="vertical" />
                </ScrollArea>

        <ServiceApiTokenModal
            v-show="openServiceApiTokenModal && serviceInEdit.id"
            @close="openServiceApiTokenModal = false"
            :is-open="openServiceApiTokenModal"
            :service-id="serviceInEdit.id"
            :service-name="serviceInEdit.name"
            :service-type="serviceInEdit.type"
            @create-token="createToken"
            @update:is-open="
                (e) => {
                    if (!e) {
                        openServiceApiTokenModal = false;
                        serviceInEdit = {};
                    }
                }
            " />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import _ from 'lodash';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import AppLoader from '../../components/AppLoader.vue';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { Edit, X, Plus, Clipboard } from 'lucide-vue-next';
import SortingButton from '../../components/global/SortingButton.vue';
import { useServicesStore } from '@/stores/services.store';
import SearchInput from '@/components/global/SearchInput.vue';
import Config from '../../utils/config';
import { getStatusColor, getStatusBadgeClasses } from '../../utils/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useRouter } from 'vue-router';
import { useConfirmBeforeAction } from '@/composables/useDialog';
import SelectDropdown from '@/components/filters/SelectDropdown.vue';
import ServiceApiTokenModal from '@/components/global/modals/ServiceApiTokenModal.vue';
import { toast } from '@/composables/useToast';
import ActionMenu from '@/components/ActionMenu.vue';
import { Input } from '@/components/ui/input';
import { TableEmptyState } from '@/components/ui/table-empty-state';

const servicesStore = useServicesStore();

// const page = ref(0);
// const pageSize = ref(0);
// const pageSize = 10;
const selectedService = ref(null);
const generalLoading = ref(false);
const sortColumn = ref('name');
const sortDirection = ref(false);
const serviceInEdit = ref({});
const openServiceApiTokenModal = ref(false);

const currentSwitchedService = ref();
const servicesSelection = ref([]);

const serviceSwitchLoading = ref({});

const router = useRouter();
const searchQuery = ref('');

const swapSorting = (column, direction) => {
    sortColumn.value = column;
    sortDirection.value = direction;
};

async function onPause(service) {
    useConfirmBeforeAction(
        'pause',
        async () => {
            try {
                // Pause service implementation
            } catch (e) {
                console.error('Error pausing service:', e);
            } finally {
                // Pause operation completed
            }
        },
        { name: service.name, actionName: 'pause' },
        () => {
            // Pause completed callback
        }
    );
}

function canPause(service) {
    return true;
}

function canResume(service) {
    return false;
}

async function onResume(service) {
    // Resume service implementation
}

async function onEdit(service) {
    // Edit service implementation
}

async function onDelete(service) {
    useConfirmBeforeAction(
        'delete',
        async () => {
            try {
                // Delete service implementation
            } catch (e) {
                console.error('Error deleting service:', e);
            } finally {
                // Delete operation completed
            }
        },
        { entity: null, name: service.name },
        () => {
            // Delete completed callback
        }
    );
}

onMounted(async () => {
    // page.value = servicesStore.page || 0;
    // pageSize.value = servicesStore.pageSize || 10;
    // initializeServicesFilters();
    generalLoading.value = true;

    try {
        await servicesStore.fetch().then(() => {
            servicesSelection.value = servicesStore.all.map((service) => {
                return {
                    value: service.id,
                    name: service.name
                };
            });

            currentSwitchedService.value = servicesStore.all?.find((service) => service.isCurrent)?.id || null;

            generalLoading.value = false;
        });
    } catch (e) {
        console.log(e);
    } finally {
        generalLoading.value = false;
    }
});

const anyServiceIsSwitching = computed(() => {
    return Object.values(serviceSwitchLoading.value).some((loading) => loading);
});

async function toggleMainService(serviceId) {
    try {
        serviceSwitchLoading.value[serviceId] = true;

        await servicesStore.switch(serviceId);

        setTimeout(() => {
            window.location.reload();
        }, 800);
    } catch (e) {
        console.log(e);
        serviceSwitchLoading.value[serviceId] = false;
    }
}

const onCreateService = () => {
    router.push({ name: 'new-service' });
};

const editServiceName = (service) => {
    serviceInEdit.value = _.cloneDeep(service);
};

const cancelEditServiceName = () => {
    serviceInEdit.value = {};
};

const saveServiceName = async () => {
    await servicesStore.editService(serviceInEdit.value);

    serviceInEdit.value = {};
};

const filteredServices = computed(() => {
    // const startIndex = page.value * pageSize;

    // const paginated = _.slice(servicesStore.all, startIndex, startIndex + pageSize);

    return _.filter(servicesStore.all, (sr) =>
        !searchQuery.value ? true : `${sr.name}.${sr.id}`.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
});

const paginationMessage = computed(() => {
    return {
        show: paginatedServices.value.length === servicesStore.all.length,
        showCustomMessage: paginatedServices.value.length !== servicesStore.all.length,
        firstPart: 'Showing services',
        middlePart: 'to',
        lastPart: `out of ${servicesStore.all.length}`
    };
});

const selectService = (service) => {
    if (selectedService.value === service) {
        selectedService.value = null;
    } else {
        selectedService.value = service;
    }
};

const onPageChange = (newPage) => {
    page.value = newPage;
};

const openTokensModal = (service) => {
    serviceInEdit.value = service;
    openServiceApiTokenModal.value = true;
};

const copyKafkaBrokers = (service) => {
    navigator.clipboard.writeText(service.kafka_broker_endpoints).then(
        () => toast.success('Kafka brokers copied to clipboard.'),
        () => toast.error('Failed to copy Kafka brokers.')
    );
};

const copyId = (id) => {
    navigator.clipboard.writeText(id).then(
        () => toast.success('Service ID copied to clipboard.'),
        () => toast.error('Failed to copy Service ID.')
    );
};
</script>

<style>
.disabled-toggle {
    cursor: not-allowed;
    opacity: 0.5;
}
</style>
